import Tag from "@/components/Tag";
import { TABLE_CARD_BODY_CELL, TableCard, TableCardMobileStack, TableCardRow } from "@/components/Table";
import { CardSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import type { CareerServicesOwnerStat } from "../queries";
import { interactionsDeltaText, ownerDisplayName, ownerShareOfMax } from "../_utils";
import { formatCount } from "../../_utils";

type Props = {
  owners: CareerServicesOwnerStat[];
  teamAvgInteractions: number | null;
  selectedOwnerId: string | null;
  onSelectOwner: (ownerId: string | null) => void;
  loading?: boolean;
};

const SELECTED_ROW_CLASSES = "bg-sys-brand-surface-container-high";

export default function TeamLeaderboard({
  owners,
  teamAvgInteractions,
  selectedOwnerId,
  onSelectOwner,
  loading = false
}: Props) {
  const maxInteractions = owners.reduce((max, entry) => Math.max(max, entry.interactions), 0);

  return (
    <TableCard title="Team performance" icon="group" testId="career-services-team-leaderboard">
      {loading && owners.length === 0 ? (
        <CardSkeleton />
      ) : owners.length === 0 ? (
        <EmptyState title="No team activity in range." iconName="group" />
      ) : (
        <table className="w-full" data-testid="career-services-team-leaderboard-table">
          <tbody>
            {owners.map((owner, index) => {
              const selectable = owner.ownerId != null;
              const selected = selectable && owner.ownerId === selectedOwnerId;
              return (
                <TableCardRow
                  key={owner.ownerId ?? "unassigned"}
                  columnCount={6}
                  className={selected ? SELECTED_ROW_CLASSES : undefined}
                  mobile={
                    <TableCardMobileStack
                      lines={[
                        `${index + 1}. ${ownerDisplayName(owner.ownerName)} · ${formatCount(owner.interactions)} interactions`,
                        `${interactionsDeltaText(owner.interactions, teamAvgInteractions)} · ${owner.topMode ?? "—"}`
                      ]}
                    />
                  }
                  onClick={selectable ? () => onSelectOwner(selected ? null : (owner.ownerId as string)) : undefined}
                  ariaLabel={
                    selectable
                      ? selected
                        ? `Clear ${ownerDisplayName(owner.ownerName)} filter`
                        : `Filter dashboard to ${ownerDisplayName(owner.ownerName)}`
                      : undefined
                  }
                >
                  <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>{index + 1}</td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    <span className={selected ? "text-body-medium-semibold" : undefined}>
                      {ownerDisplayName(owner.ownerName)}
                    </span>
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    <span className="flex items-center gap-2">
                      <span>{formatCount(owner.interactions)}</span>
                      <span
                        className="bg-sys-brand-surface-container-highest h-2 w-16 overflow-hidden rounded-full"
                        role="img"
                        aria-label={`${formatCount(owner.interactions)} interactions`}
                      >
                        <span
                          className="bg-sys-brand-primary block h-full rounded-full"
                          style={{
                            width: `${Math.round(ownerShareOfMax(owner.interactions, maxInteractions) * 100)}%`
                          }}
                        />
                      </span>
                    </span>
                  </td>
                  <td className={`${TABLE_CARD_BODY_CELL} text-sys-brand-on-surface-variant`}>
                    {interactionsDeltaText(owner.interactions, teamAvgInteractions)}
                  </td>
                  <td className={TABLE_CARD_BODY_CELL}>{owner.topMode ?? "—"}</td>
                  <td className={TABLE_CARD_BODY_CELL}>
                    {owner.topCategories.length === 0 ? (
                      "—"
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {owner.topCategories.map((category) => (
                          <Tag key={category} tagType="secondary">
                            {category}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </td>
                </TableCardRow>
              );
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  );
}
